/**
 * "Age Pension today — what sets it": which means test is setting the pension, and what would
 * have to move to lift it. One component, every module (owner, 24 Sep 2026: "I think this could be
 * handy across the board for all modules").
 *
 * READ FROM THE ENGINE'S OWN BREAKDOWN, NOT RECOMPUTED. The Age Pension assessment
 * (server/src/rest/calculation/assessment/agePension.ts) emits the rows a reader needs: the
 * counted assets, the counted income, the pension under each test, the full‑pension limits and
 * which test won ("Determined By"). Reading those rows means this note is always in the same frame
 * as the figure beside it — in care, at home, illness‑separated or not — and can never disagree
 * with it. The only arithmetic here is the taper for the worked examples, from the same constant
 * the engine uses.
 *
 * Two beats, the Support at Home pattern: the test that binds, said in one line with the reason
 * behind a dotted term; then what could change it — the full pension and a smaller lift as sizes
 * of shift, folded behind a tap because it is a lot of words — or, where the income test binds or
 * the pension is out of reach, that fact stated plainly instead of a false promise.
 */
import React from 'react';
import { PlainWordsBeat } from '../narrative/PlainWords.tsx';
import { JargonTerm } from '../narrative/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { PENSION_RATES, ANNUAL_REDUCTION_PER_1000_ASSETS } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';

export interface MeansTestBreakdownRow { label?: string; value?: unknown }

export interface MeansTestPosition {
    pension: number;
    assessableAssets: number;
    assessableIncome: number;
    assetTestPension: number;
    incomeTestPension: number;
    /** The row the engine wrote — 'Asset Test' | 'Income Test' | null when it did not say. */
    determinedBy: 'asset' | 'income' | null;
    /** The assets‑test full‑pension limit that applied, from the breakdown. */
    assetFullLimit: number | null;
    illnessSeparated: boolean;
    /** The maximum rate that applies to this household. */
    maxRate: number;
    /** How much counted assets would have to fall for the full pension (assets test binding). */
    shiftForFullPension: number | null;
    /** A smaller lift, as a worked example. */
    shiftForQuarterLift: { shift: number; pension: number } | null;
    atFullPension: boolean;
    /** True when the full pension would take shifting more than the counted assets. */
    fullOutOfReach: boolean;
}

const money = (v: unknown): number | null => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const n = parseFloat(String(v ?? '').replace(/[$,\s]/g, '').replace(/^\((.*)\)$/, '-$1'));
    return Number.isFinite(n) ? n : null;
};

/**
 * Reads the position out of a `socialSecurityBreakdown`. Null when the rows are not there — a
 * client below pension age, or a financials object from before the breakdown existed — in which
 * case the note is simply not shown, rather than guessed at.
 */
export function readMeansTestPosition(
    breakdown: MeansTestBreakdownRow[] | undefined | null,
    pension: number | undefined | null,
    isCouple: boolean,
): MeansTestPosition | null {
    if (!Array.isArray(breakdown) || breakdown.length === 0) return null;
    const rows = breakdown.filter((r) => r && typeof r.label === 'string') as Array<{ label: string; value?: unknown }>;
    const find = (label: string, nth = 0) => rows.filter((r) => r.label === label)[nth];
    const under = rows.filter((r) => r.label === 'Pension under this test');
    if (under.length < 2) return null;
    const assetTestPension = money(under[0]!.value);
    const incomeTestPension = money(under[1]!.value);
    const assessableAssets = (money(find('Your Assets')?.value) ?? 0) + (money(find('Deprived Assets')?.value) ?? 0);
    const assessableIncome = money(find('Total Assessable Income')?.value) ?? 0;
    const assetFullLimit = money(find('Full Pension Limit', 0)?.value);
    if (assetTestPension == null || incomeTestPension == null) return null;
    const determinedRow = String(find('Determined By')?.value ?? '');
    const determinedBy: MeansTestPosition['determinedBy'] = /asset/i.test(determinedRow) ? 'asset' : /income/i.test(determinedRow) ? 'income' : null;
    const illnessSeparated = String(find('Couple treatment')?.value ?? '') === 'Illness separated';
    const maxRate = !isCouple ? PENSION_RATES.single : illnessSeparated ? PENSION_RATES.single * 2 : PENSION_RATES.couple;
    const current = Number(pension) || 0;
    const assetBinds = determinedBy === 'asset' || (determinedBy == null && assetTestPension < incomeTestPension);

    let shiftForFullPension: number | null = null;
    let shiftForQuarterLift: MeansTestPosition['shiftForQuarterLift'] = null;
    let fullOutOfReach = false;
    if (assetBinds && assetFullLimit != null) {
        shiftForFullPension = Math.max(0, assessableAssets - assetFullLimit);
        fullOutOfReach = shiftForFullPension > assessableAssets;
        const target = Math.min(maxRate, current * 1.25);
        if (target > current + 1 && ANNUAL_REDUCTION_PER_1000_ASSETS > 0) {
            const requiredAssets = assetFullLimit + ((maxRate - target) / ANNUAL_REDUCTION_PER_1000_ASSETS) * 1000;
            const shift = Math.max(0, assessableAssets - requiredAssets);
            if (shift > 0 && shift < shiftForFullPension) shiftForQuarterLift = { shift, pension: target };
        }
    }
    const atFullPension = current > 0 && Math.abs(current - maxRate) <= Math.max(150, maxRate * 0.002);
    return {
        pension: current,
        assessableAssets,
        assessableIncome,
        assetTestPension,
        incomeTestPension,
        determinedBy: determinedBy ?? (assetBinds ? 'asset' : 'income'),
        assetFullLimit,
        illnessSeparated,
        maxRate,
        shiftForFullPension,
        shiftForQuarterLift,
        atFullPension,
        fullOutOfReach,
    };
}

interface MeansTestBeatsProps {
    /** The financials object the figure beside this note was read from. */
    financials: { socialSecurity?: number; socialSecurityBreakdown?: unknown } | null | undefined;
    isCouple: boolean;
    /** The client, in the second person or by name — "you" or "Cameron". */
    name: string;
    /** Extra words for the room‑to‑move beat in a module where shifting assets does something else too. */
    alsoMoves?: React.ReactNode;
    /** "Step 3" / "The strategies below" — where the reader goes to actually move the numbers. */
    whereNext?: string;
    /**
     * Name a refundable accommodation deposit among the ways money leaves the count. Only where
     * residential aged care is in play — the aged care module, or a client who has ticked it as a
     * goal. A village or downsizing client with no care in view was being told about a RAD
     * (owner, 1 Oct 2026).
     */
    careDepositRelevant?: boolean;
}

/**
 * The two beats. Renders nothing when the breakdown does not carry the rows, so a module can drop
 * it in without a guard.
 */
export function MeansTestBeats({ financials, isCouple, name, alsoMoves, whereNext = 'The strategies in step 3', careDepositRelevant = false }: MeansTestBeatsProps) {
    const pos = readMeansTestPosition(financials?.socialSecurityBreakdown as MeansTestBreakdownRow[] | undefined, financials?.socialSecurity, isCouple);
    if (!pos) return null;
    const assetBinds = pos.determinedBy === 'asset';
    // "John and Kerry are", "Robin is"; "Chris'" for a name ending in s (the household voice, 21 Oct 2026).
    const possessive = name === 'you' ? 'your' : /s$/i.test(name) ? `${name}'` : `${name}'s`;
    const nameAre = name === 'you' ? 'You are' : `${name} ${name.includes(' and ') ? 'are' : 'is'}`;
    const bindingWhy = assetBinds
        ? `Both tests are worked out and the LOWER answer wins. Here the assets test gives ${formatCurrency(pos.assetTestPension)} a year and the income test ${formatCurrency(pos.incomeTestPension)}, so the assets test sets the pension. Every $1,000 of counted assets above the full-pension limit of ${formatCurrency(pos.assetFullLimit ?? 0)} takes ${formatCurrency(ANNUAL_REDUCTION_PER_1000_ASSETS)} a year off it — which is why lowering what Centrelink counts is what lifts it.`
        : `Both tests are worked out and the LOWER answer wins. Here the income test gives ${formatCurrency(pos.incomeTestPension)} a year against the assets test's ${formatCurrency(pos.assetTestPension)}, so the income test sets the pension. Shifting assets alone will not lift it; the income Centrelink counts — deemed income on financial assets, rent, pensions — is what would have to fall.`;

    const examples = assetBinds && pos.shiftForFullPension != null && pos.shiftForFullPension > 0 && !pos.fullOutOfReach
        ? [
            `• The full pension: Centrelink counting about ${formatCurrency(pos.shiftForFullPension)} less → ${formatCurrency(pos.maxRate)} a year (up ${formatCurrency(pos.maxRate - pos.pension)}).`,
            ...(pos.shiftForQuarterLift ? [`• A quarter more: counting about ${formatCurrency(pos.shiftForQuarterLift.shift)} less → ${formatCurrency(pos.shiftForQuarterLift.pension)} a year (up ${formatCurrency(pos.shiftForQuarterLift.pension - pos.pension)}).`] : []),
        ].join('\n')
        : '';
    const stepsText = examples
        ? `These are worked examples, not recommendations — they show how much less Centrelink would need to count:\n${examples}\nMoney Centrelink doesn't count, or counts only in part: ${careDepositRelevant ? 'a refundable accommodation deposit, ' : ''}a guaranteed income for life (60% counted), a funeral bond, a gift within the yearly limit, or simply money spent. ${whereNext} are how that actually happens.`
        : '';

    return (
        <>
            <PlainWordsBeat tone="info" heading="Age Pension today — what sets it">
                <span className="font-semibold text-slate-900">{formatCurrency(pos.pension)}/yr</span>. Centrelink counts{' '}
                <span className="font-semibold text-slate-900">{formatCurrency(pos.assessableAssets)}</span> of assets and{' '}
                <span className="font-semibold text-slate-900">{formatCurrency(pos.assessableIncome)}/yr</span> of income —
                and it&apos;s the <span className="font-semibold text-slate-900">{assetBinds ? 'assets test' : 'income test'}</span>{' '}
                setting the pension <JargonTerm term="(why does that matter?)">{bindingWhy}</JargonTerm>
                {pos.illnessSeparated && (
                    <> Assessed as an <JargonTerm term="illness-separated couple" />, so each is paid at the higher single rate.</>
                )}
            </PlainWordsBeat>
            {pos.atFullPension ? (
                <PlainWordsBeat tone="good" heading="Already at the full pension">
                    {nameAre} already at the full pension — nothing to gain there, so {whereNext.toLowerCase()} are about{' '}
                    {alsoMoves ?? <>cashflow and the estate</>}.
                </PlainWordsBeat>
            ) : assetBinds && pos.fullOutOfReach ? (
                <PlainWordsBeat tone="neutral" heading="The full pension is out of reach">
                    It would take shifting more than {possessive} counted assets. A partial lift is still possible — every{' '}
                    <span className="font-semibold text-slate-900">$1,000</span> less that Centrelink counts is worth{' '}
                    <span className="font-semibold text-slate-900">{formatCurrency(ANNUAL_REDUCTION_PER_1000_ASSETS)}</span> a year
                    {alsoMoves ? <> — and it {alsoMoves}</> : null}.
                </PlainWordsBeat>
            ) : assetBinds && pos.shiftForFullPension != null && pos.shiftForFullPension > 0 ? (
                <PlainWordsBeat tone="good" heading="Room to move">
                    Centrelink doesn&apos;t count every dollar the same way. Moving money into things it counts less, or not at all,{' '}
                    <span className="font-semibold text-slate-900">lifts the Age Pension</span>
                    {alsoMoves ? <> and {alsoMoves}</> : null}. If Centrelink counted about{' '}
                    <span className="font-semibold text-slate-900">{formatCurrency(pos.shiftForFullPension)}</span> less, the full pension would be paid.
                    {stepsText && (
                        <>
                            {' '}
                            <JargonTerm term="(what shifting assets could do ›)">{stepsText}</JargonTerm>
                        </>
                    )}
                </PlainWordsBeat>
            ) : !assetBinds ? (
                <PlainWordsBeat tone="neutral" heading="The income test is what limits the pension">
                    Because the income test is what&apos;s limiting the pension, reducing assets alone won&apos;t lift it — what
                    matters here is <span className="font-semibold text-slate-900">counted income</span>: deemed income on
                    financial assets, rent, and other pensions. {whereNext} that lower counted income are the ones to look at
                    {alsoMoves ? <>, though shifting assets still {alsoMoves}</> : null}.
                </PlainWordsBeat>
            ) : null}
        </>
    );
}

export default MeansTestBeats;
